import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Chip, Stack, alpha, Collapse, useTheme, Typography } from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';

import Iconify from 'src/components/iconify';

const PERMISSION_GROUPS = [
  {
    res: 'UI_ACCESS_GROUP',
    icon: 'solar:sidebar-minimalistic-bold-duotone',
    color: '#00A76F',
    acts: [
      'ROLES',
      'USERS',
      'TICKETS',
      'DEPARTMENTS',
      'FORMS',
      'CALENDAR',
      'STATUSES',
      'ATTRIBUTES',
      'SHAREABLE_FORMS',
      'CLIENT_CREDENTIALS',
      'MY_GROUPS',
      'SETTINGS',
    ],
  },
  {
    res: 'DASHBOARD',
    icon: 'solar:graph-up-bold-duotone',
    color: '#FF4842',
    acts: ['ADMIN', 'DEPT_MANAGER', 'OPERATOR', 'CUSTOMER'],
  },
  {
    res: 'TICKET',
    icon: 'solar:ticket-bold-duotone',
    color: '#2065D1',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE', 'UPLOAD', 'SCOPE_DEPARTMENT', 'RESOLVE'],
  },
  {
    res: 'TICKET_COMMENT',
    icon: 'solar:chat-dots-bold-duotone',
    color: '#FF5630',
    acts: ['READ', 'CREATE'],
  },
  {
    res: 'TICKET_ASSIGNEES',
    icon: 'solar:users-group-two-rounded-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'USER',
    icon: 'solar:users-group-rounded-bold-duotone',
    color: '#8E33FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'ROLES',
    icon: 'solar:shield-keyhole-bold-duotone',
    color: '#7A0C2E',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'DEPARTMENT',
    icon: 'solar:structure-bold-duotone',
    color: '#FDA92D',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TAG',
    icon: 'solar:tag-price-bold-duotone',
    color: '#00A76F',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'FORM',
    icon: 'solar:document-add-bold-duotone',
    color: '#00B8D9',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'TICKET_STATUS',
    icon: 'solar:checklist-minimalistic-bold-duotone',
    color: '#3366FF',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'SHAREABLE_FORM',
    icon: 'solar:link-bold-duotone',
    color: '#003768',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'CSV',
    icon: 'ph:file-csv-bold',
    color: '#107C41',
    acts: ['UPLOAD', 'DOWNLOAD'],
  },
  {
    res: 'FILE',
    icon: 'solar:file-send-bold-duotone',
    color: '#1890FF',
    acts: ['UPLOAD', 'READ'],
  },
  {
    res: 'CLIENT_CREDENTIALS',
    icon: 'solar:key-square-bold-duotone',
    color: '#FFAB00',
    acts: ['READ', 'CREATE', 'UPDATE', 'DELETE'],
  },
  {
    res: 'PERMISSIONS',
    icon: 'solar:key-bold-duotone',
    color: '#41107C',
    acts: ['UPDATE'],
  },
  {
    res: 'EMAIL_SETTINGS',
    icon: 'solar:mailbox-bold-duotone',
    color: '#B71D18',
    acts: ['MANAGE'],
  },
];

export default function PermissionsMatrix() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userPerms = user?.permissions || [];
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [expanded, setExpanded] = useState({});

  const toggleExpand = (res) => setExpanded((p) => ({ ...p, [res]: !p[res] }));

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <Typography
        variant="subtitle1"
        sx={{
          mb: 3,
          fontWeight: 800,
          color: 'text.primary',
          px: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Iconify icon="solar:shield-user-bold-duotone" width={24} color="#00A76F" />
        {t('permissionss.editor.moduleControl')}
      </Typography>

      <Stack spacing={2.5}>
        {PERMISSION_GROUPS.map((group) => {
          const isExpanded = !!expanded[group.res];
          const isUIGroup = group.res === 'UI_ACCESS_GROUP';

          const activeActs = group.acts.filter((act) =>
            userPerms.includes(isUIGroup ? `${act}:UI_ACCESS` : `${group.res}:${act}`)
          );
          const hasAny = activeActs.length > 0;

          return (
            <Box
              key={group.res}
              sx={{
                borderRadius: '20px',
                bgcolor: 'background.paper',
                border: isExpanded
                  ? `2px solid ${group.color}`
                  : `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isExpanded
                  ? `0 12px 24px ${alpha(group.color, 0.15)}`
                  : 'none',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 2.5, cursor: 'pointer' }}
                onClick={() => toggleExpand(group.res)}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      color: hasAny ? group.color : '#919EAB',
                      display: 'flex',
                      transition: 'color 0.3s',
                    }}
                  >
                    <Iconify icon={group.icon} width={32} />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, color: 'text.primary' }}
                    >
                      {t(`permissionss.modules.${group.res}.title`)}
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      useFlexGap
                      spacing={0.8}
                      mt={0.5}
                    >
                      {hasAny ? (
                        activeActs.map((act) => (
                          <Chip
                            key={act}
                            label={t(`permissionss.actions.${act}.short`)}
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              fontWeight: 900,
                              height: '18px',
                              bgcolor: alpha(group.color, 0.08),
                              color: group.color,
                            }}
                          />
                        ))
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                        >
                          {t('permissionss.editor.noAccess')}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Stack>
                <Iconify
                  icon={
                    isExpanded
                      ? 'solar:alt-arrow-up-bold-duotone'
                      : 'solar:alt-arrow-down-bold-duotone'
                  }
                  color="text.disabled"
                  width={20}
                />
              </Stack>

              {/* MODULE CONTENT */}
              <Collapse in={isExpanded}>
                <Box
                  sx={{
                    px: 3,
                    pb: 3,
                    bgcolor: 'background.neutral',
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack spacing={0.5} mt={2}>
                    {group.acts.map((action) => {
                      const k = isUIGroup
                        ? `${action}:UI_ACCESS`
                        : `${group.res}:${action}`;
                      const has = userPerms.includes(k);

                      return (
                        <Stack
                          key={k}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ py: 1.5, px: 2, borderRadius: '12px' }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                color: has ? 'text.primary' : 'text.disabled',
                              }}
                            >
                              {t(
                                `permissionss.modules.${
                                  isUIGroup ? action : group.res
                                }.actions.${isUIGroup ? 'UI_ACCESS' : action}.title`
                              )}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: 'text.secondary', display: 'block' }}
                            >
                              {t(
                                `permissionss.modules.${
                                  isUIGroup ? action : group.res
                                }.actions.${isUIGroup ? 'UI_ACCESS' : action}.desc`
                              )}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              p: 0.8,
                              borderRadius: '10px',
                              bgcolor: has
                                ? alpha(group.color, 0.1)
                                : isDark
                                ? alpha(theme.palette.action.disabled, 0.05)
                                : 'rgba(145, 158, 171, 0.08)',
                              color: has ? group.color : 'text.disabled',
                            }}
                          >
                            <Iconify
                              icon={
                                has
                                  ? 'solar:check-read-bold'
                                  : 'solar:forbidden-circle-bold'
                              }
                              width={20}
                            />
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
